import { supabase } from "@/integrations/supabase/client";

const SESSION_KEY = "esr_session_id";
const SESSION_TTL_MS = 30 * 60 * 1000; // 30 min inactivity

function uuid() {
  return crypto.randomUUID();
}

function getSessionId(): string {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    const now = Date.now();
    if (raw) {
      const { id, ts } = JSON.parse(raw);
      if (now - ts < SESSION_TTL_MS) {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, ts: now }));
        return id;
      }
    }
    const id = uuid();
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, ts: now }));
    return id;
  } catch {
    return uuid();
  }
}

function detectDevice(): string {
  const ua = navigator.userAgent;
  if (/Mobi|Android|iPhone/i.test(ua)) return "Mobile";
  if (/iPad|Tablet/i.test(ua)) return "Tablet";
  return "Desktop";
}

function detectSource(referrer: string): string {
  if (!referrer) return "Direct";
  try {
    const host = new URL(referrer).hostname.replace(/^www\./, "");
    if (host.includes(window.location.hostname)) return "Direct";
    return host;
  } catch {
    return "Direct";
  }
}

let lastPath: string | null = null;
let lastEnterTime = Date.now();
let lastEventId: string | null = null;

async function flushDuration() {
  if (!lastEventId) return;
  const duration = Date.now() - lastEnterTime;
  try {
    await supabase
      .from("pageview_events")
      .update({ duration_ms: duration })
      .eq("id", lastEventId);
  } catch {
    // best effort
  }
}

// Detect bots, crawlers, headless browsers, and scrapers by User-Agent.
// We still SERVE pages to them (so LLMs like GPTBot, ClaudeBot, PerplexityBot
// can index the site) — we just don't COUNT them in analytics.
function isBot(): boolean {
  const ua = (navigator.userAgent || "").toLowerCase();
  if (!ua) return true;

  const botPatterns = [
    // Generic
    "bot", "crawler", "spider", "crawling", "scrape", "scraper",
    // Headless / automation
    "headless", "phantomjs", "puppeteer", "playwright", "selenium", "webdriver",
    "http-client", "okhttp", "python-requests", "axios", "node-fetch", "curl", "wget",
    "go-http", "java/", "libwww", "lwp", "ruby", "perl",
    // Search & LLM crawlers (excluded from human counts but still allowed to load the site)
    "googlebot", "bingbot", "yandex", "baidu", "duckduckbot", "applebot",
    "gptbot", "oai-searchbot", "chatgpt-user", "claudebot", "claude-web",
    "anthropic-ai", "perplexitybot", "perplexity-user", "google-extended",
    "ccbot", "facebookexternalhit", "twitterbot", "linkedinbot", "slackbot",
    "telegrambot", "whatsapp", "discordbot",
    // SEO / monitoring
    "ahrefs", "semrush", "mj12bot", "dotbot", "petalbot", "bytespider",
    "amazonbot", "uptimerobot", "pingdom", "datadog",
  ];

  if (botPatterns.some((p) => ua.includes(p))) return true;

  // Headless Chrome signature
  if ("webdriver" in navigator && (navigator as { webdriver?: boolean }).webdriver) return true;

  return false;
}

export async function trackPageview(path: string) {
  // Skip admin & portal routes — internal traffic
  if (path.startsWith("/admin") || path.startsWith("/portal")) return;
  if (path === lastPath) return;

  // Skip bots/crawlers/headless — they shouldn't pollute human analytics.
  // (The page itself still renders for them, so LLM/search indexing is unaffected.)
  if (isBot()) return;

  await flushDuration();

  lastPath = path;
  lastEnterTime = Date.now();

  const country =
    (Intl.DateTimeFormat().resolvedOptions().timeZone || "").split("/")[1] || "Unknown";

  // NOTE: do NOT use .select() here — the table's SELECT policy is admin-only,
  // so asking PostgREST to return the inserted row makes the whole insert fail.
  // We generate the id client-side instead so duration updates can target it.
  const eventId = uuid();
  try {
    const { error } = await supabase.from("pageview_events").insert({
      id: eventId,
      session_id: getSessionId(),
      path,
      referrer: document.referrer || null,
      source: detectSource(document.referrer),
      device: detectDevice(),
      country,
      user_agent: navigator.userAgent,
    });
    lastEventId = error ? null : eventId;
  } catch {
    lastEventId = null;
  }

}

export function initPageviewTracker() {
  // Flush duration on tab close
  window.addEventListener("beforeunload", () => {
    if (!lastEventId) return;
    const duration = Date.now() - lastEnterTime;
    navigator.sendBeacon?.(
      `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/rest/v1/pageview_events?id=eq.${lastEventId}`,
      new Blob([JSON.stringify({ duration_ms: duration })], { type: "application/json" })
    );
  });
}
